import React, { useState } from "react";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { SunIcon, MoonIcon } from "../DaytimeIcon";
import { Day, TimeRange, WeeklyCalendarProps } from "./types";
import {
  DAYS,
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  END_HOUR_OPTIONS,
  HOUR_OPTIONS,
  MINUTE_OPTIONS,
  TOTAL_SLOTS,
} from "./constants";
import {
  canCopyAllEventsToDay,
  clampRangeToWindow,
  findAvailableEventSlot,
  getDayEnabledRange,
  isDayDisabled,
  isEventVisible,
  slotIndexToTime,
  timeToSlotIndex,
} from "./utils";
import { useScheduleActions } from "./useScheduleActions";
import { useDeleteEventConfirmation } from "./useDeleteEventConfirmation";
import WeeklyCalendarEventModal from "./WeeklyCalendarEventModal";
import DeleteEventConfirmDialog from "./DeleteEventConfirmDialog";
import {
  AddEventButton,
  MobileCopyButton,
  MobileDayBox,
  MobileDayLabel,
  MobileDayRow,
  MobileDeleteButton,
  MobileEventRow,
  MobileEventTime,
  MobileHint,
  MobileList,
  TimeSelect,
  TimeSelectGroup,
  TimeSeparator,
} from "./WeeklyCalendarMobile.styles";

const WeeklyCalendarMobile: React.FC<WeeklyCalendarProps> = ({
  weekdaysEnabled = false,
  weekendEnabled = false,
  showSun = false,
  showMoon = false,
  showCopyPreviousDay = false,
  editEventsInModal = false,
  confirmDeleteEvent = false,
  clickableDayHeader = false,
  showEmptyHint = false,
  startTime = DEFAULT_START_TIME,
  endTime = DEFAULT_END_TIME,
  value,
  onChange,
}) => {
  const { copyFromPreviousDay, deleteEvent } = useScheduleActions(
    value,
    onChange,
    weekdaysEnabled,
    weekendEnabled,
    startTime,
    endTime
  );

  const [modal, setModal] = useState<{
    day: Day;
    eventIndex: number;
    initialEvent: TimeRange;
  } | null>(null);

  const { requestDelete, pending, onConfirm, onCancel } =
    useDeleteEventConfirmation(deleteEvent, confirmDeleteEvent);

  const handleAddEvent = (day: Day) => {
    const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
    if (!range) return;
    const dayRanges = value[day.key] || [];
    const newEvent = findAvailableEventSlot(dayRanges, range);
    if (!newEvent) return;
    onChange({ ...value, [day.key]: [...dayRanges, newEvent] });
  };

  const openCreateModal = (day: Day) => {
    const range = clampRangeToWindow(
      getDayEnabledRange(day, weekdaysEnabled, weekendEnabled),
      startTime,
      endTime
    );
    if (!range) return;
    const dayRanges = value[day.key] || [];
    const proposed = findAvailableEventSlot(dayRanges, range) || [
      slotIndexToTime(range.minSlot),
      slotIndexToTime(Math.min(range.minSlot + 2, range.maxSlot)),
    ];
    setModal({ day, eventIndex: -1, initialEvent: proposed });
  };

  const openEditModal = (day: Day, eventIndex: number, event: TimeRange) => {
    setModal({ day, eventIndex, initialEvent: event });
  };

  const handleModalSave = (event: TimeRange) => {
    if (!modal) return;
    const dayRanges = [...(value[modal.day.key] || [])];
    if (modal.eventIndex === -1) {
      dayRanges.push(event);
    } else {
      dayRanges[modal.eventIndex] = event;
    }
    onChange({ ...value, [modal.day.key]: dayRanges });
    setModal(null);
  };

  const handleTimeChange = (
    day: Day,
    eventIndex: number,
    field: "startH" | "startM" | "endH" | "endM",
    val: string
  ) => {
    const dayRanges = [...(value[day.key] || [])];
    const event = [...dayRanges[eventIndex]] as TimeRange;
    const [startH, startM] = event[0].split(":");
    const [endH, endM] = event[1].split(":");

    let nSH = startH, nSM = startM, nEH = endH, nEM = endM;
    switch (field) {
      case "startH": nSH = val; break;
      case "startM": nSM = val; break;
      case "endH": nEH = val; if (val === "24") nEM = "00"; break;
      case "endM": nEM = val; break;
    }

    const newStartIdx = timeToSlotIndex(`${nSH}:${nSM}`);
    let newEndIdx = timeToSlotIndex(`${nEH}:${nEM}`);

    const isStartChange = field === "startH" || field === "startM";

    // Si al mover la hora de inicio la franja queda invertida (fin <= inicio),
    // desplazamos también la hora fin para mantener la duración original.
    // Al modificar la hora fin dejándola anterior al inicio no se permite el cambio.
    if (newStartIdx >= newEndIdx) {
      if (!isStartChange) return;
      const duration = timeToSlotIndex(event[1]) - timeToSlotIndex(event[0]);
      newEndIdx = Math.min(newStartIdx + duration, TOTAL_SLOTS);
    }

    const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
    if (range && (newStartIdx < range.minSlot || newEndIdx > range.maxSlot)) return;

    const overlaps = dayRanges.some((r, idx) => {
      if (idx === eventIndex) return false;
      const s = timeToSlotIndex(r[0]);
      const e = timeToSlotIndex(r[1]);
      return newStartIdx < e && s < newEndIdx;
    });
    if (overlaps) return;

    dayRanges[eventIndex] = [slotIndexToTime(newStartIdx), slotIndexToTime(newEndIdx)];
    onChange({ ...value, [day.key]: dayRanges });
  };

  return (
    <MobileList>
      {DAYS.map((day, dayIndex) => {
        const range = getDayEnabledRange(day, weekdaysEnabled, weekendEnabled);
        const disabled = !range;
        const events = (!disabled && value[day.key]) || [];
        const isFirstDay = dayIndex === 0;
        const prevDay = !isFirstDay ? DAYS[dayIndex - 1] : null;
        const prevDayDisabled = prevDay ? isDayDisabled(prevDay, weekdaysEnabled, weekendEnabled) : true;
        const prevDayEvents = prevDay
          ? (value[prevDay.key] || []).filter((event) =>
              isEventVisible(event, startTime, endTime)
            )
          : [];
        const currentRange = clampRangeToWindow(range, startTime, endTime);
        const canCopy = !isFirstDay && !disabled && prevDayEvents.length > 0 && !prevDayDisabled && currentRange !== null && canCopyAllEventsToDay(prevDayEvents, currentRange, events);
        const hasVisibleEvents = events.some((event) =>
          isEventVisible(event, startTime, endTime)
        );
        const startHours = range
          ? HOUR_OPTIONS.filter(h => { const n = parseInt(h); return n >= Math.floor(range.minSlot / 2) && n <= Math.floor((range.maxSlot - 1) / 2); })
          : HOUR_OPTIONS;
        const endHours = range
          ? END_HOUR_OPTIONS.filter(h => { const n = parseInt(h); return n >= Math.floor(range.minSlot / 2) && n <= Math.floor(range.maxSlot / 2); })
          : END_HOUR_OPTIONS;
        return (
          <MobileDayBox key={day.key}>
            <MobileDayRow
              $disabled={disabled}
              $clickable={clickableDayHeader && !disabled}
              onClick={
                clickableDayHeader && !disabled
                  ? () => openCreateModal(day)
                  : undefined
              }
            >
              <MobileDayLabel>{day.fullLabel}</MobileDayLabel>
              {showCopyPreviousDay && (
                <MobileCopyButton
                  $disabled={!canCopy}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (canCopy) copyFromPreviousDay(dayIndex);
                  }}
                >
                  <FileCopyIcon style={{ fontSize: 12 }} />
                  Copiar del día anterior
                </MobileCopyButton>
              )}
            </MobileDayRow>
            {events.map((event, idx) => {
              if (!isEventVisible(event, startTime, endTime)) return null;
              const [sH, sM] = event[0].split(":");
              const [eH, eM] = event[1].split(":");
              const startSlot = timeToSlotIndex(event[0]);
              const endSlot = timeToSlotIndex(event[1]);
              const startIsDay = startSlot >= 18 && startSlot < 42;
              const endIsDay = endSlot > 18 && endSlot <= 42;
              const hasDaytime = startIsDay || endIsDay;
              const hasNighttime = !startIsDay || !endIsDay;
              const icons = startIsDay ? (
                <>{hasDaytime && showSun && <SunIcon fontSize={14} />}{hasNighttime && showMoon && <MoonIcon fontSize={14} />}</>
              ) : (
                <>{hasNighttime && showMoon && <MoonIcon fontSize={14} />}{hasDaytime && showSun && <SunIcon fontSize={14} />}</>
              );
              const deleteButton = (
                <MobileDeleteButton
                  onClick={(e) => {
                    e.stopPropagation();
                    requestDelete(day, idx, event);
                  }}
                >
                  Eliminar
                </MobileDeleteButton>
              );
              if (editEventsInModal) {
                return (
                  <MobileEventRow
                    key={idx}
                    $clickable
                    onClick={() => openEditModal(day, idx, event)}
                  >
                    {icons}
                    <MobileEventTime>
                      Desde <strong>{event[0]}</strong>, hasta{" "}
                      <strong>{event[1]}</strong>
                    </MobileEventTime>
                    {deleteButton}
                  </MobileEventRow>
                );
              }
              return (
                <MobileEventRow key={idx}>
                  {icons}
                  <TimeSelectGroup>
                    <TimeSelect value={sH} onChange={(e) => handleTimeChange(day, idx, "startH", e.target.value)}>
                      {startHours.map((h) => <option key={h} value={h}>{h}</option>)}
                    </TimeSelect>
                    :
                    <TimeSelect value={sM} onChange={(e) => handleTimeChange(day, idx, "startM", e.target.value)}>
                      {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </TimeSelect>
                  </TimeSelectGroup>
                  <TimeSeparator>-</TimeSeparator>
                  <TimeSelectGroup>
                    <TimeSelect value={eH} onChange={(e) => handleTimeChange(day, idx, "endH", e.target.value)}>
                      {endHours.map((h) => <option key={h} value={h}>{h}</option>)}
                    </TimeSelect>
                    :
                    <TimeSelect
                      value={eM}
                      disabled={eH === "24"}
                      onChange={(e) => handleTimeChange(day, idx, "endM", e.target.value)}
                    >
                      {MINUTE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                    </TimeSelect>
                  </TimeSelectGroup>
                  {deleteButton}
                </MobileEventRow>
              );
            })}
            {disabled && showEmptyHint && (
              <MobileHint>No disponible</MobileHint>
            )}
            {!disabled && showEmptyHint && !hasVisibleEvents && (
              <MobileHint>Sin franjas configuradas</MobileHint>
            )}
            {!disabled && (
              <AddEventButton
                onClick={() =>
                  editEventsInModal
                    ? openCreateModal(day)
                    : handleAddEvent(day)
                }
              >
                + Añadir franja horaria
              </AddEventButton>
            )}
          </MobileDayBox>
        );
      })}
      {modal && (
        <WeeklyCalendarEventModal
          day={modal.day}
          initialEvent={modal.initialEvent}
          eventIndex={modal.eventIndex}
          dayEvents={value[modal.day.key] || []}
          range={clampRangeToWindow(
            getDayEnabledRange(modal.day, weekdaysEnabled, weekendEnabled),
            startTime,
            endTime
          )}
          onSave={handleModalSave}
          onClose={() => setModal(null)}
        />
      )}
      {pending && (
        <DeleteEventConfirmDialog
          day={pending.day}
          event={pending.event}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      )}
    </MobileList>
  );
};

export default WeeklyCalendarMobile;
